import React, { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Backbone from 'backbone';
import { QueryClientProvider } from '@tanstack/react-query';
import { LDProvider, useFlags } from 'launchdarkly-react-client-sdk';
import { AppTopbar } from './app-topbar';
import { AppSidebar } from './app-sidebar/app-sidebar';
import { AppSidebarExtended } from './app-sidebar/app-sidebar-extended';
import { getNavigationConfig } from './app-sidebar/navigation-config';
import { getSettingsNavConfig } from './app-sidebar/settings-nav-config';
import { getV2NavigationConfig } from './app-sidebar/v2-nav-config';
import { filterNavigationConfig } from './app-sidebar/utils/permission-filter';
import { useSidebarStore, SidebarProvider } from '../../stores/sidebar-store';
import { SIDEBAR_DIMENSIONS } from './app-sidebar/constants';
import { AppTopbarUserMenu } from '../topbar/app-topbar-user-menu';
import { AppSwitcher } from '../topbar/app-switcher';
import { getPageName, getPageBadge } from './app-sidebar/utils/get-page-name';
import { UserType } from '../../../types';
import {
  TopbarContentProvider,
  setTopbarRightContentGlobal,
} from './topbar-content-context';
import { getNewAdminUrl } from '../../utils/urls';
import { isV2User } from '../../utils/rbac';
import { useTenantRbacDoc } from '../../api/useTenantRbacDoc';
import { defaultQueryClient } from '../../../query_client';
import {
  CLIENT_SIDE_ID,
  getContext,
} from '../../../integrations/utils/launchdarkly';
import {
  SalesCopilotModalProvider,
  useSalesCopilotModalStore,
} from '../../stores/sales-copilot-modal-store';
import { SalesCopilotModal } from './app-sidebar/sales-copilot-modal';
import {
  OrgContextBanner,
  ORG_CONTEXT_BANNER_HEIGHT_PX,
} from './org-context-banner';

interface NewLayoutWrapperProps {
  tenantId: string;
  primaryTenantId?: string;
  flags?: {
    demo?: boolean;
    onboarding_intercom?: boolean;
    onboarding_hubspot?: boolean;
    onboarding_googlesheet?: boolean;
    profiles_demo?: boolean;
    enableMadapi?: boolean;
  };
  user?:
    | UserType
    | {
        email?: string;
        superKudu?: boolean;
        role?: string;
      };
  logo?: React.ReactNode;
  topbarRightContent?: React.ReactNode;
}

/**
 * Component that sets the --sidebar-width CSS variable on root element
 * Must be inside SidebarProvider to access the same state as the sidebar
 */
function SidebarWidthSetter(): null {
  const { isCollapsed } = useSidebarStore();

  React.useEffect(() => {
    const sidebarWidthPx = isCollapsed
      ? SIDEBAR_DIMENSIONS.MAIN_COLLAPSED_PX
      : SIDEBAR_DIMENSIONS.MAIN_EXPANDED_PX;
    document.documentElement.style.setProperty(
      '--sidebar-width',
      `${sidebarWidthPx}px`
    );
  }, [isCollapsed]);

  return null;
}

/**
 * Inner component that uses the SalesCopilotModalStore hook.
 * This must be inside SalesCopilotModalProvider.
 */
function NewLayoutWrapperContent({
  tenantId,
  primaryTenantId,
  flags = {},
  user,
  topbarRightContent,
}: NewLayoutWrapperProps) {
  const [pathname, setPathname] = useState(window.location.pathname);
  const showOrgContextBanner = Boolean(
    user?.superKudu === true &&
      tenantId &&
      (!primaryTenantId || tenantId !== primaryTenantId)
  );
  const {
    isOpen: isSalesCopilotModalOpen,
    openModal: openSalesCopilotModal,
    closeModal: closeSalesCopilotModal,
  } = useSalesCopilotModalStore();

  // v2 RBAC detection. Mirrors mk-admin-app's `_sidebar.tsx`: v2 users always
  // get the v2 layout; SuperKudu falls back to v2 only when the tenant doc
  // (org-level rbac) has hydrated. v1 users keep the legacy nav unchanged.
  const { data: rbacDoc } = useTenantRbacDoc(tenantId);
  const userForRbac = user as UserType | undefined;
  const useV2 = isV2User(userForRbac) || (!!user?.superKudu && !!rbacDoc);

  // Merge bongo flags (already in `flags` prop as a string list) with LD
  // flags so v2-nav-config can read the same flag names mk-admin-app uses.
  const ldFlags = useFlags();
  const v2Flags = {
    enableMadapi: !!ldFlags?.enableMadapi,
    tenantProductsManagement: !!ldFlags?.tenantProductsManagement,
    intentWidgetAndSettingsPage: !!ldFlags?.intentWidgetAndSettingsPage,
  };

  const navigationConfig = useV2
    ? getV2NavigationConfig({
        tenantId,
        user: user as UserType | undefined,
        flags: v2Flags,
      })
    : getNavigationConfig({
        tenantId,
        flags,
        user,
        onSalesCopilotClick: openSalesCopilotModal,
      });

  // Apply v2 role/module/tier gating so the rendered sidebar matches
  // mk-admin-app exactly (e.g. non-admin Data Studio users don't see
  // admin-only sections). Legacy (v1) nav is left untouched.
  const sidebarConfig = useMemo(() => {
    if (!useV2) return navigationConfig;
    const {
      filteredSections,
      filteredStandaloneLinks,
      filteredBottomLinks,
    } = filterNavigationConfig(
      navigationConfig,
      user as UserType | undefined,
      tenantId
    );
    return {
      sections: filteredSections,
      standaloneLinks: filteredStandaloneLinks,
      bottomLinks: filteredBottomLinks,
    };
  }, [useV2, navigationConfig, user, tenantId]);

  // Check if we're on a settings route
  const isSettingsRoute = useMemo(() => {
    return (
      pathname.includes('/settings') ||
      pathname.includes('/flags') ||
      pathname.includes('/processes') ||
      pathname.includes('/push') ||
      pathname.includes('/prototypes') ||
      pathname.includes('/campaigns')
    );
  }, [pathname]);

  const settingsNavConfig = useMemo(() => {
    if (!isSettingsRoute) return null;
    return getSettingsNavConfig({
      tenantId,
      user: user as UserType | undefined,
      flags,
    });
  }, [isSettingsRoute, tenantId, user, flags]);

  const pageName = useMemo(() => {
    if (isSettingsRoute) {
      return 'Settings';
    }
    if (settingsNavConfig) {
      const name = getPageName(pathname, settingsNavConfig);
      if (name) return name;
    }
    return getPageName(pathname, navigationConfig);
  }, [pathname, navigationConfig, isSettingsRoute, settingsNavConfig]);

  const pageBadge = useMemo(() => {
    if (isSettingsRoute && settingsNavConfig) {
      const badge = getPageBadge(pathname, settingsNavConfig);
      if (badge) return badge;
    }
    return getPageBadge(pathname, navigationConfig);
  }, [pathname, navigationConfig, isSettingsRoute, settingsNavConfig]);

  useEffect(() => {
    // Listen to Backbone history changes
    const handleRouteChange = () => {
      setPathname(window.location.pathname);
    };

    // Listen to popstate events (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);

    // Listen to Backbone history events
    Backbone.history.on('route', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      Backbone.history.off('route', handleRouteChange);
    };
  }, []);

  const handleNavigate = (href: string, isExternal: boolean) => {
    if (isExternal) {
      // Check if it's a new admin URL - redirect instead of opening in new tab
      const newAdminUrl = getNewAdminUrl();
      if (href.startsWith(newAdminUrl)) {
        window.location.assign(href);
        return;
      }
      // For other external links, open in new tab
      window.open(href, '_blank');
      return;
    }

    // Use Backbone navigation for internal links
    Backbone.history.navigate(href, { trigger: true });
    setPathname(href);
  };

  // Sidebar logo - the 2026 lockup (coral mark, navy wordmark), 30px tall
  const logoElement = (
    <img
      src="/media/images/hg-logo-2026.svg"
      alt="HG Insights"
      style={{ height: '30px', objectFit: 'contain' }}
    />
  );

  // Initialize Document360 Eddy AI widget
  useEffect(() => {
    // Check if script already exists
    if (document.getElementById('mw')) return;

    // Document360 knowledge base widget - inject as raw script
    const script = document.createElement('script');
    script.innerHTML = `
      (function (w,d,s,o,f,js,fjs) {
          w['JS-Widget']=o;w[o] = w[o] || function () { (w[o].q = w[o].q || []).push(arguments) };
          js = d.createElement(s), fjs = d.getElementsByTagName(s)[0];
          js.id = o; js.src = f; js.async = 1; fjs.parentNode.insertBefore(js, fjs);
      }(window, document, 'script', 'mw', 'https://cdn.document360.io/static/js/widget.js'));
      mw('init', { apiKey: '1v9fUFV3l+4XZ+laMwCP8ktA+djQvUhnMztmA4GXuYWDtTBRNleQy1OWi9wovgURzRkbdPm6cPnpkZzNERhEMucOEQYLUA+QWw7uUVBPZY6pXqLKeIB7xWxy3rjst3emPrCNK0oxcO4QZ7ASYQzlcg==' });
    `;
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Remove the Document360 script
      const existingScript = document.getElementById('mw');
      if (existingScript) {
        existingScript.remove();
      }
      // Remove our injected script
      const scripts = document.head.querySelectorAll('script');
      scripts.forEach((s) => {
        if (s.innerHTML.includes('JS-Widget')) {
          s.remove();
        }
      });
    };
  }, []);

  // Topbar has no leftContent in new admin - logo is only in sidebar
  // Topbar right content: UserMenu only (CopilotIcon hidden in favor of Eddy AI)
  const topbarRightContentFinal =
    topbarRightContent ||
    (user?.email ? (
      <React.Fragment>
        <AppTopbarUserMenu tenantId={tenantId} />
      </React.Fragment>
    ) : null);

  // Set rightContent in context so custom topbar content can access it
  React.useEffect(() => {
    setTopbarRightContentGlobal(topbarRightContentFinal);
  }, [topbarRightContentFinal]);

  return (
    <SidebarProvider>
      <SidebarWidthSetter />
      <TopbarContentProvider>
        {showOrgContextBanner && <OrgContextBanner tenantId={tenantId} />}

        {/* Container: flex row - sidebar (full height) + right side (topbar + content) */}
        <div
          className="tw-flex tw-w-full tw-overflow-hidden"
          style={{
            height: showOrgContextBanner
              ? `calc(100vh - ${ORG_CONTEXT_BANNER_HEIGHT_PX}px)`
              : '100vh',
            marginTop: showOrgContextBanner
              ? `${ORG_CONTEXT_BANNER_HEIGHT_PX}px`
              : undefined,
          }}
        >
          {/* Sidebar - full height */}
          <AppSidebar
            config={sidebarConfig}
            logo={logoElement}
            logoHref={`/org/${tenantId}`}
            pathname={pathname}
            onNavigate={handleNavigate}
          />

          {/* Right side: flex column - topbar + content */}
          <div className="tw-flex tw-flex-col tw-flex-1 tw-min-w-0 tw-min-h-0 tw-overflow-hidden">
            {/* Topbar - fixed height */}
            <AppTopbar
              leftContent={
                useV2 ? (
                  <AppSwitcher
                    tenantId={tenantId}
                    user={user as UserType | undefined}
                    rbacDoc={rbacDoc ?? null}
                  />
                ) : undefined
              }
              rightContent={topbarRightContentFinal}
              pageName={useV2 ? null : pageName}
              badgeText={useV2 ? null : pageBadge}
            />

            {/* Content area: flex row - extended sidebar + pageContent */}
            <div className="tw-flex tw-flex-1 tw-min-w-0 tw-min-h-0 tw-overflow-hidden">
              {/* Extended Sidebar (if settings route) */}
              {isSettingsRoute && settingsNavConfig && (
                <AppSidebarExtended
                  config={settingsNavConfig}
                  pathname={pathname}
                  tenantId={tenantId}
                  user={user as UserType | undefined}
                  onNavigate={handleNavigate}
                />
              )}

              {/* PageContent - Backbone renders into .main-wrapper */}
              <div
                id="page-content-wrapper"
                className="tw-flex-1 tw-min-w-0 tw-min-h-0 tw-overflow-x-hidden tw-overflow-y-auto"
              />
            </div>
          </div>
        </div>

        <SalesCopilotModal
          open={isSalesCopilotModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              closeSalesCopilotModal();
            }
          }}
        />
      </TopbarContentProvider>
    </SidebarProvider>
  );
}

/**
 * React wrapper component that integrates the new layout with Backbone/Marionette.
 * This component renders the topbar and sidebar, while main content is handled by Backbone.
 *
 * Wraps the tree in QueryClientProvider so v2 RBAC hooks (`useTenantRbacDoc`)
 * work at layout level, and in LDProvider so v2-nav-config can read the same
 * LD flags mk-admin-app uses.
 */
export function NewLayoutWrapper(props: NewLayoutWrapperProps) {
  const tenantNumeric = Number(props.tenantId);
  const ldContext = Number.isFinite(tenantNumeric)
    ? getContext(tenantNumeric)
    : { kind: 'tenant' as const, key: '0', id: 0 };

  return (
    <QueryClientProvider client={defaultQueryClient}>
      <LDProvider
        clientSideID={CLIENT_SIDE_ID}
        context={ldContext}
        timeout={5000}
      >
        <SalesCopilotModalProvider>
          <NewLayoutWrapperContent {...props} />
        </SalesCopilotModalProvider>
      </LDProvider>
    </QueryClientProvider>
  );
}

/**
 * Component that moves .main-wrapper into the flex layout and applies styles
 */
function MainLayoutStyles(): null {
  React.useEffect(() => {
    const applyStyles = () => {
      const mainWrapper = document.querySelector(
        '.main-wrapper'
      ) as HTMLElement;
      const pageContentWrapper = document.getElementById(
        'page-content-wrapper'
      );

      if (mainWrapper && pageContentWrapper) {
        // Move .main-wrapper into the page content wrapper if it's not already there
        if (mainWrapper.parentElement !== pageContentWrapper) {
          pageContentWrapper.appendChild(mainWrapper);
        }

        // Remove classes that might add margin-left or left positioning
        mainWrapper.classList.remove('sidebar-visible', 'subnav-visible');

        // Apply styles for flex layout - explicitly remove all margins
        const style = {
          padding: '2rem',
          width: '100%',
          height: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          margin: '0',
          marginLeft: '0',
          marginRight: '0',
          marginTop: '0',
          marginBottom: '0',
          left: 'auto',
          right: 'auto',
          position: 'relative',
          boxSizing: 'border-box',
        };

        Object.assign(mainWrapper.style, style);
      }
    };

    // Apply styles immediately
    applyStyles();

    // Watch for changes to .main-wrapper (Backbone may recreate it)
    const observer = new MutationObserver(() => {
      applyStyles();
    });

    // Observe the page content wrapper for child changes
    const pageContentWrapper = document.getElementById('page-content-wrapper');
    if (pageContentWrapper) {
      observer.observe(pageContentWrapper, {
        childList: true,
        subtree: true,
      });
    }

    // Also observe document body in case .main-wrapper is added elsewhere
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Periodic check to ensure styles are always applied (fallback)
    const intervalId = setInterval(applyStyles, 500);

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, []);

  return null;
}

/**
 * Initialize the new layout by rendering it into the DOM.
 * This should be called from the router's set_layout method.
 */
export function initializeNewLayout(options: NewLayoutWrapperProps) {
  // Create a container for the new layout (topbar + sidebar)
  // This will be rendered outside of Backbone regions
  let layoutContainer = document.getElementById('new-layout-container');
  if (!layoutContainer) {
    layoutContainer = document.createElement('div');
    layoutContainer.id = 'new-layout-container';
    // Insert before the #app element
    const appElement = document.getElementById('app');
    if (appElement && appElement.parentNode) {
      appElement.parentNode.insertBefore(layoutContainer, appElement);
    } else {
      document.body.insertBefore(layoutContainer, document.body.firstChild);
    }
  }

  // Hide the old sidebar if it exists
  const oldSidebar = document.getElementById('sidebar');
  if (oldSidebar) {
    oldSidebar.style.display = 'none';
  }

  // Prevent page scroll; the new layout handles scrolling internally.
  document.documentElement.classList.add('new-layout-enabled');
  document.body.classList.add('new-layout-enabled');

  // Render the new layout (topbar + sidebar)
  ReactDOM.render(<NewLayoutWrapper {...options} />, layoutContainer);

  // Apply AppLayout styles to the main content area
  // Create a hidden container to render the style manager component
  let styleContainer = document.getElementById('main-layout-styles');
  if (!styleContainer) {
    styleContainer = document.createElement('div');
    styleContainer.id = 'main-layout-styles';
    styleContainer.style.display = 'none';
    document.body.appendChild(styleContainer);
  }

  ReactDOM.render(
    <SidebarProvider>
      <MainLayoutStyles />
    </SidebarProvider>,
    styleContainer
  );
}
